import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  Store as StoreIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { ChatWindowProps } from '../types/chat';
import MessageBubble from './MessageBubble';
import chatService from '../services/chatService';
import { CHAT_CHA } from '../theme/chatChaavi';
import { VCH } from '../theme/vendorChatChaavi';

const ChatWindow: React.FC<ChatWindowProps> = ({
  chat,
  messages,
  currentUserPhone,
  onSendMessage,
  onMarkAsRead,
  isLoading = false,
  onCloseThread,
  variant = 'default',
}) => {
  const navigate = useNavigate();
  const isChaavi = variant === 'chaavi';
  const isVendorChaavi = variant === 'vendor-chaavi';
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isSending, setIsSending] = useState(false);
  const [canVendorSend, setCanVendorSend] = useState(true);
  const [checkingPermissions, setCheckingPermissions] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const checkVendorPermissions = React.useCallback(async () => {
    const isVendor = chat.vendorPhone === currentUserPhone;
    if (!isVendor) return;

    setCheckingPermissions(true);
    try {
      const canSend = await chatService.canVendorSendMessage(chat.chatId, currentUserPhone);
      setCanVendorSend(canSend);
    } catch (error) {
      console.error('Error checking vendor permissions:', error);
      setCanVendorSend(false);
    } finally {
      setCheckingPermissions(false);
    }
  }, [chat.vendorPhone, currentUserPhone, chat.chatId]);

  useEffect(() => {
    onMarkAsRead();
    checkVendorPermissions();
  }, [chat.chatId, onMarkAsRead, checkVendorPermissions]);

  const handleSendMessage = async () => {
    if (newMessage.trim() && !isSending) {
      const isVendor = chat.vendorPhone === currentUserPhone;

      if (isVendor && !canVendorSend) {
        alert('You can only reply to client messages. Please wait for the client to send the first message.');
        return;
      }

      setIsSending(true);
      try {
        await onSendMessage(newMessage.trim());
        setNewMessage('');

        if (isVendor) {
          checkVendorPermissions();
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        if (error instanceof Error && error.message.includes('Vendors can only reply')) {
          alert(error.message);
        }
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleBack = () => {
    if (onCloseThread) onCloseThread();
    else window.history.back();
  };

  const getOtherParticipantInfo = () => {
    const isClient = chat.clientPhone === currentUserPhone;
    return {
      phone: isClient ? chat.vendorPhone : chat.clientPhone,
      name: isClient ? chat.otherParticipantName || 'Vendor' : chat.otherParticipantName || 'Client',
      type: isClient ? 'VENDOR' : 'CLIENT',
    };
  };

  const otherParticipant = getOtherParticipantInfo();

  const clientInitial = (otherParticipant.name || 'C').charAt(0).toUpperCase();

  const vendorMessageNodes = useMemo(() => {
    if (!isVendorChaavi) return null;
    let lastDateKey = '';
    const nodes: React.ReactNode[] = [];
    for (const message of messages) {
      const dk = new Date(message.createdAt).toDateString();
      if (dk !== lastDateKey) {
        lastDateKey = dk;
        const label = new Date(message.createdAt).toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        });
        nodes.push(
          <Box key={`divider-${message.messageId}`} sx={{ textAlign: 'center', my: 1.5 }}>
            <Typography
              sx={{
                display: 'inline-block',
                fontSize: 11,
                color: VCH.dateMuted,
                fontFamily: 'sans-serif',
                px: 1.25,
                py: 0.25,
                borderRadius: '8px',
                bgcolor: 'rgba(245,240,232,0.85)',
              }}
            >
              {label}
            </Typography>
          </Box>
        );
      }
      nodes.push(
        <MessageBubble
          key={message.messageId}
          message={message}
          isOwnMessage={message.senderPhone === currentUserPhone}
          showSenderName={false}
          variant="vendor-chaavi"
        />
      );
    }
    return nodes;
  }, [isVendorChaavi, messages, currentUserPhone]);

  if (isVendorChaavi) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: VCH.chatAreaBg }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `0.5px solid ${VCH.border}`,
            bgcolor: VCH.chatAreaBg,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleBack}
            size="small"
            sx={{
              display: onCloseThread ? { xs: 'inline-flex', md: 'none' } : { xs: 'inline-flex', sm: 'none' },
              color: VCH.barMuted,
            }}
            aria-label="Back to conversations"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: '#f0ebe0',
              color: VCH.barMuted,
              fontFamily: 'sans-serif',
            }}
          >
            {clientInitial}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: VCH.ink, fontFamily: 'sans-serif' }} noWrap>
              {otherParticipant.name}
            </Typography>
            <Typography sx={{ fontSize: 11, color: VCH.muted, fontFamily: 'sans-serif' }} noWrap>
              {chat.businessName}
              {chat.orderId ? ` · Order #${chat.orderId}` : ''}
            </Typography>
          </Box>
          {chat.orderId ? (
            <Button
              size="small"
              onClick={() =>
                navigate('/vendor-orders-notifications', {
                  state: { activeTab: 1, orderId: String(chat.orderId) },
                })
              }
              sx={{
                flexShrink: 0,
                fontSize: 11,
                textTransform: 'none',
                color: VCH.accent,
                border: `0.5px solid ${VCH.border}`,
                borderRadius: '8px',
                px: 1,
                minWidth: 0,
              }}
            >
              View order
            </Button>
          ) : null}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: VCH.noticeBg,
            borderBottom: `0.5px solid ${VCH.noticeBorder}`,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 11, color: VCH.noticeInk, fontFamily: 'sans-serif', lineHeight: 1.45 }}>
            Clients must send the first message in each chat. You can reply once they have reached out.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2, minHeight: 0, bgcolor: VCH.chatAreaBg }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" minHeight={200}>
              <CircularProgress size={28} sx={{ color: VCH.accent }} />
            </Box>
          ) : messages.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              minHeight={200}
              sx={{ color: VCH.muted }}
            >
              <Typography sx={{ fontSize: 15, fontWeight: 500, color: VCH.ink, fontFamily: 'sans-serif' }}>
                No messages yet
              </Typography>
              <Typography sx={{ fontSize: 12, mt: 0.5, fontFamily: 'sans-serif', textAlign: 'center', maxWidth: 280 }}>
                Waiting for {otherParticipant.name} to send the first message.
              </Typography>
            </Box>
          ) : (
            <Box>
              {vendorMessageNodes}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderTop: `0.5px solid ${VCH.border}`,
            bgcolor: VCH.chatAreaBg,
            flexShrink: 0,
          }}
        >
          {chat.vendorPhone === currentUserPhone && !canVendorSend && (
            <Alert
              severity="info"
              icon={<BlockIcon />}
              sx={{ mb: 1.5, fontSize: 12 }}
              action={
                <Button size="small" onClick={checkVendorPermissions} disabled={checkingPermissions}>
                  {checkingPermissions ? 'Checking...' : 'Refresh'}
                </Button>
              }
            >
              You can only reply after the client sends the first message.
            </Alert>
          )}
          <Box display="flex" gap={1} alignItems="center">
            <IconButton size="small" sx={{ color: VCH.muted }} aria-label="Attach file" disabled>
              <AttachFileIcon fontSize="small" />
            </IconButton>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={
                chat.vendorPhone === currentUserPhone && !canVendorSend
                  ? 'Waiting for client to send first message…'
                  : `Message ${otherParticipant.name}…`
              }
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSending || (chat.vendorPhone === currentUserPhone && !canVendorSend)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '22px',
                  bgcolor: '#fff',
                  fontSize: 13,
                  fontFamily: 'sans-serif',
                  '& fieldset': { borderColor: VCH.border },
                },
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || isSending || (chat.vendorPhone === currentUserPhone && !canVendorSend)}
              sx={{
                width: 40,
                height: 40,
                bgcolor: VCH.accent,
                color: '#fff',
                '&:hover': { bgcolor: VCH.accentHover },
                '&:disabled': { bgcolor: '#e0d8cc', color: '#a89880' },
              }}
              aria-label="Send message"
            >
              {isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }

  const header = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderRadius: 0,
        borderBottom: `1px solid ${isChaavi ? CHAT_CHA.border : undefined}`,
        bgcolor: isChaavi ? CHAT_CHA.shellBg : undefined,
        boxShadow: isChaavi ? 'none' : 2,
      }}
    >
      <IconButton
        onClick={handleBack}
        sx={{
          display: onCloseThread ? { xs: 'inline-flex', md: 'none' } : { xs: 'inline-flex', sm: 'none' },
          color: CHAT_CHA.text,
        }}
        aria-label="Back to conversations"
      >
        <ArrowBackIcon />
      </IconButton>

      <Avatar
        sx={{
          bgcolor: isChaavi
            ? CHAT_CHA.gold
            : otherParticipant.type === 'VENDOR'
              ? 'secondary.main'
              : 'primary.main',
          color: isChaavi ? '#fff' : undefined,
        }}
      >
        {otherParticipant.type === 'VENDOR' ? <StoreIcon /> : <PersonIcon />}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="h6"
          noWrap
          sx={{
            fontFamily: isChaavi ? '"Playfair Display", serif' : undefined,
            fontWeight: 400,
            color: isChaavi ? CHAT_CHA.text : undefined,
          }}
        >
          {otherParticipant.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ color: isChaavi ? CHAT_CHA.textMuted : undefined }}>
          {chat.businessName}
        </Typography>
      </Box>

      {chat.orderId && (
        <Chip
          label="Order"
          size="small"
          variant="outlined"
          sx={
            isChaavi
              ? {
                  borderColor: CHAT_CHA.border,
                  color: CHAT_CHA.goldDark,
                }
              : { color: 'primary.main' }
          }
        />
      )}
    </Paper>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: isChaavi ? CHAT_CHA.shellBg : undefined }}>
      {header}

      {!isChaavi && <Divider />}

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          bgcolor: isChaavi ? CHAT_CHA.threadBg : 'grey.50',
          minHeight: 0,
        }}
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" minHeight={200}>
            <CircularProgress sx={{ color: isChaavi ? CHAT_CHA.gold : undefined }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            minHeight={200}
            sx={{ color: isChaavi ? CHAT_CHA.textMuted : 'text.secondary' }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontFamily: isChaavi ? '"Playfair Display", serif' : undefined }}>
              No messages yet
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: isChaavi ? '"Jost", sans-serif' : undefined }}>
              Start a conversation with {otherParticipant.name}
            </Typography>
          </Box>
        ) : (
          <Box>
            {messages.map((message) => (
              <MessageBubble
                key={message.messageId}
                message={message}
                isOwnMessage={message.senderPhone === currentUserPhone}
                showSenderName={false}
                variant={isChaavi ? 'chaavi' : 'default'}
              />
            ))}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {!isChaavi && <Divider />}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 0,
          borderTop: isChaavi ? `1px solid ${CHAT_CHA.border}` : undefined,
          bgcolor: isChaavi ? CHAT_CHA.shellBg : undefined,
          boxShadow: isChaavi ? 'none' : 2,
        }}
      >
        {chat.vendorPhone === currentUserPhone && !canVendorSend && (
          <Alert
            severity="info"
            icon={<BlockIcon />}
            sx={{ mb: 2 }}
            action={
              <Button size="small" onClick={checkVendorPermissions} disabled={checkingPermissions}>
                {checkingPermissions ? 'Checking...' : 'Refresh'}
              </Button>
            }
          >
            You can only reply to client messages. Please wait for the client to send the first message.
          </Alert>
        )}

        <Box display="flex" gap={1} alignItems="flex-end">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={
              chat.vendorPhone === currentUserPhone && !canVendorSend
                ? 'Waiting for client to send first message...'
                : `Message ${otherParticipant.name}…`
            }
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSending || (chat.vendorPhone === currentUserPhone && !canVendorSend)}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                bgcolor: isChaavi ? '#fff' : undefined,
                fontFamily: isChaavi ? '"Jost", sans-serif' : undefined,
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || isSending || (chat.vendorPhone === currentUserPhone && !canVendorSend)}
            sx={{
              bgcolor: isChaavi ? CHAT_CHA.gold : 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: isChaavi ? CHAT_CHA.goldDark : 'primary.dark',
              },
              '&:disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500',
              },
            }}
          >
            {isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChatWindow;
